import { Button, List, ListItem, ListItemText, ListSubheader, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Link, Route, Switch } from 'react-router-dom';

import { useAppUser } from 'components/AppUserProvider';
import { analytics } from 'FirebaseConfigs';
import { AnalyticsEvent } from 'models/AnalyticsEvent';
import { AppRoles } from 'models/AppRoles';
import { Paths } from 'Paths';
import { Dashboard } from 'screens/Dashboard';
import { EmptyPage } from 'screens/EmptyPage';
import { HelpChats } from 'screens/HelpChats';
import { HelpMessages } from 'screens/HelpMessages';
import { CreateOffer } from 'screens/Offer/CreateOffer/CreateOffer';
import { Offers } from 'screens/Offer/Offers';
import { OfferScreen } from 'screens/Offer/OfferScreen';
import { OrgScreen } from 'screens/OrgScreen';
import { OrgsScreen } from 'screens/OrgsScreen';
import { ImportPriceFieldsScreen } from 'screens/PriceField/ImportTemplatesPriceFieldsScreen';
import { PriceFieldTemplatesScreen } from 'screens/PriceField/PriceFieldTemplatesScreen';
import { SelectOrgScreen } from 'screens/SelectOrgScreen';

import { useOrgUser } from './components/OrgUserProvider';
import { PageNavigation } from './components/PageNavigation';
import { OrgRoles } from './models/OrgRoles';
import { MyAppUserScreen } from './MyAppUserScreen';
import { MyOrgScreen } from './screens/MyOrgScreen';
import { MyOrgUserScreen } from './screens/MyOrgUserScreen';
import { DraftOfferScreen } from './screens/Offer/DraftOfferScreen';
import { PriceFieldScreen, PriceFieldTemplateScreen, PriceFieldTemplateSuggestionScreen } from './screens/PriceField/PriceFieldScreen';
import { PriceFieldsScreen } from './screens/PriceField/PriceFieldsScreen';
import { AppUserScreen } from './screens/Users/AppUserScreen';
import { AppUsersScreen } from './screens/Users/AppUsersScreen';
import { OrgUserScreen } from './screens/Users/OrgUserScreen';
import { OrgUsersScreen } from './screens/Users/OrgUsersScreen';

type AuthorizedRouteProps = {
    children: JSX.Element
    roles?: OrgRoles.OrgRole[]
    appRoles?: AppRoles.AppRole[]
}

function AuthorizedRoute({ children, roles, appRoles }: AuthorizedRouteProps) {
    const [orgUser] = useOrgUser()
    const [appUser] = useAppUser()
    const missingOrgRole = (orgUser && !!roles?.some(role => !orgUser.data.orgRoles?.map[role]))
    const missingAppRole = (appUser && !!appRoles?.some(role => !appUser.data.appRoles?.map[role]))

    if (missingOrgRole || missingAppRole) {
        return (
            <PageNavigation title="Ingen tilgang">
                <List>
                    <ListItem>
                        <Typography variant="h6">Du har ikke tilgang til denne siden</Typography>
                    </ListItem>
                    <ListItem>
                        <Typography variant="subtitle1">For å få tilgang må du ha minst en av disse</Typography>
                    </ListItem>
                    <ListSubheader>Roller</ListSubheader>
                    {roles?.map(role => (
                        <ListItem key={role}>
                            <ListItemText primary={OrgRoles.Flat[role].name} secondary={OrgRoles.Flat[role].description} />
                        </ListItem>
                    ))}
                    {appRoles?.map(role => (
                        <ListItem key={role}>
                            <ListItemText primary={AppRoles.Flat[role].name} secondary={AppRoles.Flat[role].description} />
                        </ListItem>
                    ))}
                    {(orgUser && missingOrgRole) ? (
                        <ListItem>
                            <Button
                                variant="contained"
                                component={Link}
                                color="primary"
                                fullWidth
                                to={Paths.ORG_USERS + '/' + orgUser.data.id}
                            >Se dine org roller</Button>
                        </ListItem>
                    ) : null}
                    {(appUser && missingAppRole) ? (
                        <ListItem>
                            <Button
                                variant="contained"
                                component={Link}
                                color="primary"
                                fullWidth
                                to={Paths.APP_USERS + '/' + appUser.data.id}
                            >Se dine app roller</Button>
                        </ListItem>
                    ) : null}
                </List>
            </PageNavigation>
        )
    }
    return children
}

export function Routes() {
    return (
        <ErrorBoundary onError={(error, info) => {
            analytics.logEvent(AnalyticsEvent.ERROR_BOUNDARY_ACTIVATION, { error, info })
            console.error(error)
            console.error(info)
        }} FallbackComponent={({ resetErrorBoundary, error }) => (
            <Alert style={{ width: '100%' }} severity="warning">
                {'Oisann! Her skjedde det noe rart. Trykk'}
                <Button color="primary" onClick={resetErrorBoundary}>her</Button>{' for å prøve på nytt eller'}
                <Button color="primary" onClick={() => window.location.reload()}>her</Button>
                {' for å fornye siden: '}
                {error.message}
            </Alert>
        )}>
            <Switch>
                <Route path={Paths.MY_ORG}>
                    <AuthorizedRoute roles={[OrgRoles.FlatEnum.OWNER]}>
                        <MyOrgScreen />
                    </AuthorizedRoute>
                </Route>
                <Route exact path={Paths.ROOT} component={Dashboard} />
                <Route path={Paths.DASHBOARD} component={Dashboard} />
                <Route path={Paths.OWN_HELP_MESSAGES}>
                    <HelpMessages isOwn />
                </Route>
                <Route exact path={Paths.HELP_MESSAGES + "/:userId/"} render={({ match }) => <HelpMessages userId={match.params.userId} />} />
                <Route path={Paths.HELP_MESSAGES}>
                    <AuthorizedRoute appRoles={[AppRoles.FlatEnum.ADMIN]}>
                        <HelpChats />
                    </AuthorizedRoute>
                </Route>
                <Route exact path={Paths.ORG_USERS} component={OrgUsersScreen}></Route>
                <Route exact path="/org-brukere/:userId/" render={({ match }) => <OrgUserScreen id={match.params.userId} />} />
                <Route exact path="/app-brukere/:userId/" render={({ match }) => <AppUserScreen id={match.params.userId} />} />
                <Route path={Paths.CREATE_OFFER} component={CreateOffer} />
                <Route exact path={Paths.OFFER} component={Offers} />
                <Route exact path={Paths.OFFER_DRAFTS}>
                    <Offers drafts />
                </Route>
                <Route exact path={Paths.PRICE_FIELDS} component={PriceFieldsScreen}></Route>
                <Route exact path={Paths.IMPORT_PRICE_FIELD_TEMPLATES} component={ImportPriceFieldsScreen}></Route>
                <Route exact path={Paths.PRICE_FIELD_TEMPLATES + '/:templateId/'} render={({ match }) => <PriceFieldTemplateScreen id={match.params.templateId} />} />
                <Route exact path={Paths.PRICE_FIELD_TEMPLATE_SUGGESTIONS + '/:templateId/'} render={({ match }) => <PriceFieldTemplateSuggestionScreen id={match.params.templateId} />} />
                <Route exact path={Paths.CREATE_PRICE_FIELD_TEMPLATE} ><PriceFieldTemplateScreen createNew /></Route>
                <Route exact path={Paths.CREATE_PRICE_FIELD_TEMPLATE_SUGGESTION} ><PriceFieldTemplateSuggestionScreen createNew /></Route>
                <Route exact path={Paths.PRICE_FIELD_TEMPLATES} component={PriceFieldTemplatesScreen}></Route>
                <Route exact path={Paths.PRICE_FIELD_TEMPLATE_SUGGESTIONS}>
                    <PriceFieldTemplatesScreen suggestions />
                </Route>
                <Route exact path="/prisfelter/:priceFieldId/" render={({ match }) => <PriceFieldScreen id={match.params.priceFieldId} />} />
                <Route exact path={Paths.CREATE_PRICE_FIELD}>
                    <AuthorizedRoute roles={[OrgRoles.FlatEnum.OWNER]}>
                        <PriceFieldScreen createNew />
                    </AuthorizedRoute>
                </Route>
                <Route exact path={Paths.APP_USERS}><AppUsersScreen /></Route>
                <Route exact path={Paths.ORGS}>
                    <AuthorizedRoute appRoles={[AppRoles.FlatEnum.ADMIN]}>
                        <OrgsScreen />
                    </AuthorizedRoute>
                </Route>
                <Route exact path="/organisasjoner/:orgId/" render={({ match }) => <OrgScreen id={match.params.orgId} />} />
                <Route exact path={Paths.MY_ORG_USER}><MyOrgUserScreen /></Route>
                <Route exact path={Paths.MY_APP_USER}><MyAppUserScreen /></Route>
                <Route exact path={Paths.SELECT_ORG}><SelectOrgScreen /></Route>
                <Route
                    path="/anbud/:offerId/"
                    render={({ match }) => <OfferScreen offerId={match.params.offerId} />} />
                <Route
                    path="/utkast/anbud/:offerId/"
                    render={({ match }) => <DraftOfferScreen offerId={match.params.offerId} />} />
                <Route path={Paths.ROOT} component={EmptyPage} />
            </Switch>
        </ErrorBoundary>
    )
}